import './WeatherWidget.css';
import React, { useState, useEffect, useRef } from 'react';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import ComponentsWrapper from '../atoms/ComponentsWrapper';
import { getColorByTheme, getPageThemeColors, get, hexToRgb, canUseDOM, getDictionaryItem } from '../../utils';
import { IconArrowDown, IconCalendar, IconHumidity, IconSunrise, IconSunset, IconTempMax, IconTempMin, IconWind } from './icons';
import { DatePickerDropDown, emptyDateShowObj, getStringFullDate, getStringWeek } from './components/DatePicker';
import debounce from 'lodash/debounce';
import CarouselProvider from '../atoms/CarouselProvider/CarouselProvider';
import { isNil } from '../../utils/IsNil';
import { convertToAnalyticObject } from '../../utils/analytics';
import { tealiumClickFunc, tealiumMergeClickData } from '../../utils/tealium';
import { useSitecoreContext } from '../../utils/SitecoreContextReactContext';
function isSelectedDateGreaterThanCurrentFor14Days(date) {
  const timeDifference = date.getTime() - new Date().getTime();
  const daysDifference = timeDifference / (1000 * 3600 * 24);
  return daysDifference >= 14;
}
const isTimeIsNow = date => {
  const todayDate = new Date();
  const isDateEqual = todayDate.toISOString().slice(0, 10) === date.toISOString().slice(0, 10);
  const isHoursEqual = todayDate.getHours() === date.getHours();
  return isDateEqual && isHoursEqual;
};
const convertTime = timeStr => {
  if (timeStr && (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm'))) {
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':');
    if (hours === '12') {
      hours = '00';
    }
    if (modifier === 'PM') {
      hours = parseInt(hours, 10) + 12;
    }
    return `${hours}:${minutes}`;
  }
  return timeStr;
};
const defaultSelectedForecast = {
  sunrise: '',
  sunset: '',
  avgHumidity: '',
  maxWind: '',
  far: {
    tempMin: '',
    tempMax: ''
  },
  cel: {
    tempMin: '',
    tempMax: ''
  },
  hours: [{
    time: '',
    far: {
      temp: ''
    },
    cel: {
      temp: ''
    }
  }]
};
const defaultTodayForecast = {
  conditionIcon: '',
  conditionText: '',
  far: {
    temp: ''
  },
  cel: {
    temp: ''
  }
};
const defaultWeeklyForecast = [{
  conditionIcon: '',
  conditionText: '',
  dayOfWeek: '',
  far: {
    tempMin: '',
    tempMax: ''
  },
  cel: {
    tempMin: '',
    tempMax: ''
  }
}];
const WeatherWidget = _ref => {
  let {
    fields,
    params
  } = _ref;
  const {
    dictionary,
    datasource
  } = fields.data;
  const sitecoreContext = useSitecoreContext();
  const lang = sitecoreContext && sitecoreContext.language || 'en';
  const rtl = Boolean(sitecoreContext && sitecoreContext.rtl);
  const pageEditing = Boolean(sitecoreContext && sitecoreContext.pageEditing);
  const apiKeyValue = get(sitecoreContext, "configuration.weatherApi.apiKey");
  const apiUrlValue = get(sitecoreContext, "configuration.weatherApi.apiUrl");
  const defaultLocationValue = get(sitecoreContext, "configuration.weatherApi.defaultLocation");
  const jssComponentErrorMessage = getDictionaryItem(dictionary, "errorMessage") || "Something went wrong";
  const dateMobileDateText = getDictionaryItem(dictionary, 'dateMobileSelectDate') || 'Select date';
  const weeklyTitleText = getDictionaryItem(dictionary, 'weeklyTitle') || 'This week';
  const todayText = getDictionaryItem(dictionary, 'today') || 'Today';
  const celcius = getDictionaryItem(dictionary, 'celcius') || "Celcius";
  const farenheit = getDictionaryItem(dictionary, 'farenheit') || "Farenheit";
  const sunrise = getDictionaryItem(dictionary, 'sunrise') || "Sunrise";
  const sunset = getDictionaryItem(dictionary, 'sunset') || "Sunset";
  const low = getDictionaryItem(dictionary, 'low') || "Low";
  const high = getDictionaryItem(dictionary, 'high') || "High";
  const humidity = getDictionaryItem(dictionary, 'humidity') || 'Humidity';
  const wind = getDictionaryItem(dictionary, 'wind') || "Wind";
  const windSpeed = getDictionaryItem(dictionary, 'windSpeed') || 'km/h';
  const hoursNow = getDictionaryItem(dictionary, 'now') || 'Now';
  const heading = get(datasource, 'heading.jss');
  const [isDataProcessing, setIsDataProcessing] = useState(true);
  const [isVdsite, setIsVdsite] = useState(true);
  const [todayForecast, setTodayForecast] = useState(defaultTodayForecast);
  const [selectedForecast, setSelectedForecast] = useState(defaultSelectedForecast);
  const [weeklyForecast, setWeeklyForecast] = useState(defaultWeeklyForecast);
  const [degreeMode, setDegreeMode] = useState('cel');
  const [error, setError] = useState('');
  const [serviceError, setServiceError] = useState('');
  const [departureDate, setDepartureDate] = useState(emptyDateShowObj);
  const [dateOpened, setDateOpened] = useState(false);
  const [isMobile, setIsMobile] = useState(canUseDOM ? window.matchMedia(`(max-width: 767px)`).matches : false);
  const [hoursNowIndex, setHoursNowIndex] = useState(-1);
  const hoursRef = useRef(null);
  const listRef = useRef(null);
  let eventsAnalytics = get(fields, 'data.eventsAnalytics') || [];
  if (eventsAnalytics.length) {
    eventsAnalytics = convertToAnalyticObject(eventsAnalytics);
  }
  const openCalendar = () => {
    setDateOpened(true);
    tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['clickWeatherCalendarAnalytics'], params), "open");
  };
  const closeCalendar = () => {
    setDateOpened(false);
    tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['clickWeatherCalendarAnalytics'], params), "close");
  };
  const debouncedCheckMobile = debounce(() => {
    setIsMobile(window.matchMedia(`(max-width: 767px)`).matches);
  }, 250);
  const setTodayForecastCallback = data => setTodayForecast({
    conditionIcon: get(data, 'current.condition.icon', '').replace('//cdn.weatherapi.com/weather', '/-/media/Weather'),
    conditionText: get(data, 'current.condition.text'),
    cel: {
      temp: get(data, 'current.temp_c')
    },
    far: {
      temp: get(data, 'current.temp_f')
    }
  });
  const setSelectedForecastCallback = data => {
    const hours = get(data, 'forecast.forecastday.0.hour').map(hour => ({
      time: hour.time,
      far: {
        temp: hour.temp_f
      },
      cel: {
        temp: hour.temp_c
      }
    }));
    return setSelectedForecast({
      sunrise: convertTime(get(data, 'forecast.forecastday.0.astro.sunrise')),
      sunset: convertTime(get(data, 'forecast.forecastday.0.astro.sunset')),
      avgHumidity: get(data, 'forecast.forecastday.0.day.avghumidity'),
      maxWind: get(data, 'forecast.forecastday.0.day.maxwind_kph'),
      far: {
        tempMin: get(data, 'forecast.forecastday.0.day.mintemp_f'),
        tempMax: get(data, 'forecast.forecastday.0.day.maxtemp_f')
      },
      cel: {
        tempMin: get(data, 'forecast.forecastday.0.day.mintemp_c'),
        tempMax: get(data, 'forecast.forecastday.0.day.maxtemp_c')
      },
      hours
    });
  };
  const setWeeklyForecastCallback = data => {
    const forecastday = get(data, 'forecast.forecastday').reduce((forecastList, currentDay, index) => {
      // skip current day
      if (index === 0) {
        return forecastList;
      }
      const {
        condition,
        maxtemp_c,
        maxtemp_f,
        mintemp_c,
        mintemp_f
      } = currentDay.day;
      const dataAboutDay = {
        dayOfWeek: new Intl.DateTimeFormat(lang, {
          weekday: "long"
        }).format(new Date(currentDay.date)),
        conditionIcon: condition.icon.replace('//cdn.weatherapi.com/weather', '/-/media/Weather'),
        conditionText: condition.text,
        far: {
          tempMin: mintemp_f,
          tempMax: maxtemp_f
        },
        cel: {
          tempMin: mintemp_c,
          tempMax: maxtemp_c
        }
      };
      forecastList.push(dataAboutDay);
      return forecastList;
    }, []);
    return setWeeklyForecast(forecastday);
  };
  const selectDateWeatherCalendarAnalytics = date => {
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: "long",
      year: 'numeric'
    }).replace(/ /g, '-');
    tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['selectDateWeatherCalendarAnalytics'], params), formattedDate);
  };
  const changeWeatherDataHandler = date => {
    let url;
    if (isSelectedDateGreaterThanCurrentFor14Days(date)) {
      url = new URL(`${apiUrlValue}/future.json`);
    } else {
      url = new URL(`${apiUrlValue}/forecast.json`);
    }
    const queryString = new URLSearchParams({
      key: apiKeyValue,
      q: defaultLocationValue,
      lang: lang,
      aqi: 'no',
      alerts: 'no',
      dt: date.toISOString().slice(0, 10)
    });
    url.search = queryString.toString();
    fetch(url).then(response => response.json()).then(data => {
      if (data.error) {
        setServiceError(pageEditing ? get(data, 'error.message') : jssComponentErrorMessage);
      } else {
        setSelectedForecastCallback(data);
      }
    });
    selectDateWeatherCalendarAnalytics(date);
  };
  const getWeatherData = () => {
    const url = new URL(`${apiUrlValue}/forecast.json`);
    const queryString = new URLSearchParams({
      key: apiKeyValue,
      q: defaultLocationValue,
      lang: lang,
      days: '7',
      aqi: 'no',
      alerts: 'no'
    });
    url.search = queryString.toString();
    fetch(url).then(response => response.json()).then(data => {
      if (data.error) {
        setServiceError(pageEditing ? get(data, 'error.message') : jssComponentErrorMessage);
      } else {
        setTodayForecastCallback(data);
        setSelectedForecastCallback(data);
        setWeeklyForecastCallback(data);
      }
    }).catch(() => setError(jssComponentErrorMessage)).finally(() => setIsDataProcessing(false));
  };
  const hideDateSelect = e => {
    const mobileDevice = window.matchMedia(`(max-width: 767px)`).matches;
    if (mobileDevice || e.target.closest('.fields-wrapper__date') || e.target.closest('.datepicker-dropdown') || e.target.closest('.react-datepicker__day')) return;
    setDateOpened(false);
  };
  useEffect(() => {
    if (canUseDOM) {
      if (!document.body.classList.contains('vdsite')) {
        setIsVdsite(false);
      }
      return () => {};
    }
  }, []);
  useEffect(() => {
    if (apiKeyValue) {
      getWeatherData();
    }
  }, [apiKeyValue]);
  useEffect(() => {
    if (canUseDOM) {
      window.addEventListener('resize', debouncedCheckMobile);
      document.addEventListener("click", hideDateSelect);
      return () => {
        window.removeEventListener('resize', debouncedCheckMobile);
        document.addEventListener("click", hideDateSelect);
      };
    }
  }, []);
  useEffect(() => {
    if (hoursRef.current) {
      setHoursNowIndex(Number(hoursRef.current.id));
    }
  }, [hoursRef.current]);
  if (isNil(apiKeyValue) || isNil(apiUrlValue) || isNil(defaultLocationValue)) {
    console.warn('Weather widget component render skipped. No apikey provided');
    if (pageEditing) {
      return /*#__PURE__*/React.createElement("div", {
        className: "no-data"
      }, "[No apikey for weather widget provided]");
    }
    return null;
  }
  const dateFieldProps = {
    lang: lang,
    rtl: rtl,
    dateOpened: dateOpened,
    closeDateDropdown: () => setDateOpened(false),
    closeDateDropdownWithAnalytics: closeCalendar,
    setDepDate: setDepartureDate,
    isMobile: isMobile,
    dateMobileDateText: dateMobileDateText,
    changeWeatherDataHandler
  };
  const getSelectedDay = rtl => {
    if (rtl) {
      return `
      ${departureDate.month}
        ${departureDate.day},
        ${getStringFullDate(departureDate.fullDate) === getStringFullDate(new Date()) ? todayText : getStringWeek(departureDate.fullDate)}`;
    }
    return `${getStringFullDate(departureDate.fullDate) === getStringFullDate(new Date()) ? todayText : getStringWeek(departureDate.fullDate)},
        ${departureDate.day}
        ${departureDate.month}`;
  };
  const renderItems = () => selectedForecast.hours.map((hour, index) => {
    const date = new Date(hour.time.toString());
    return /*#__PURE__*/React.createElement("div", {
      key: index,
      className: "listicle-item"
    }, isTimeIsNow(date) ? /*#__PURE__*/React.createElement("div", {
      ref: hoursRef,
      id: index.toString(),
      className: "renderItemTimeNow"
    }, hoursNow) : /*#__PURE__*/React.createElement("div", {
      className: "renderItemTime"
    }, hour.time.split(" ")[1]), /*#__PURE__*/React.createElement("div", {
      className: "renderItemTemp"
    }, hour[degreeMode].temp, "\xB0"));
  });
  const renderSuccess = () => {
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "d-flex weatherContainer"
    }, /*#__PURE__*/React.createElement("div", {
      className: "col-lg-5"
    }, !isDataProcessing ? /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-main-layout"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-main-item fr-wr-main-item--current-temp-mode"
    }, /*#__PURE__*/React.createElement("div", {
      className: "todayForecastCondition"
    }, todayForecast.conditionText), /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-main-item__current-temp"
    }, todayForecast[degreeMode].temp, "\xB0"), /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-main-item__mode"
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => {
        setDegreeMode('cel');
      },
      className: `${degreeMode === 'cel' ? 'active' : ''}`
    }, celcius), /*#__PURE__*/React.createElement("button", {
      onClick: () => {
        setDegreeMode('far');
      },
      className: `${degreeMode === 'far' ? 'active' : ''}`
    }, farenheit))), /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-main-item"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-main-item__icon-holder"
    }, /*#__PURE__*/React.createElement("img", {
      src: todayForecast.conditionIcon.replace('64x64', '128x128'),
      alt: todayForecast.conditionText
    })))) : null), /*#__PURE__*/React.createElement("div", {
      className: "forecastSelect"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fields-wrapper__date fields-wrapper__date--single"
    }, /*#__PURE__*/React.createElement("div", {
      tabIndex: 0,
      className: "row fields-wrapper__date-departure",
      onClick: openCalendar
    }, /*#__PURE__*/React.createElement("div", {
      className: rtl ? "row-reverse" : "row"
    }, /*#__PURE__*/React.createElement(IconCalendar, {
      className: "iconCalendarCommon"
    }), /*#__PURE__*/React.createElement("div", {
      className: "row-reverse"
    }, getSelectedDay(rtl))), /*#__PURE__*/React.createElement(IconArrowDown, {
      className: "icon-arrow m-0"
    })), dateOpened && /*#__PURE__*/React.createElement("div", {
      className: "fields-wrapper__date-arrow"
    }), /*#__PURE__*/React.createElement(DatePickerDropDown, dateFieldProps)), /*#__PURE__*/React.createElement(CarouselProvider, {
      activeItemIndex: hoursNowIndex
    }, _ref2 => {
      let {
        renderCarousel,
        renderPreviousButton,
        renderNextButton
      } = _ref2;
      return /*#__PURE__*/React.createElement("div", {
        className: "hoursForecastSlider"
      }, renderCarousel(renderItems(), {
        responsive: {
          desktop: {
            breakpoint: {
              max: 10000,
              min: 769
            },
            items: 2
          },
          table: {
            breakpoint: {
              max: 768,
              min: 461
            },
            items: 3
          },
          mobile: {
            breakpoint: {
              max: 460,
              min: 0
            },
            items: 2
          }
        },
        slidesToSlide: 2
      }), /*#__PURE__*/React.createElement("div", {
        className: "listicle__navigation-container"
      }, renderPreviousButton(), renderNextButton()));
    }))), /*#__PURE__*/React.createElement("div", {
      className: "col-lg-12"
    }, !isDataProcessing ? /*#__PURE__*/React.createElement("div", {
      className: "row currentCardDetailsWrapper"
    }, /*#__PURE__*/React.createElement("div", {
      className: "row currentCardDetailsItem"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cardImage"
    }, /*#__PURE__*/React.createElement(IconSunrise, null)), /*#__PURE__*/React.createElement("div", {
      className: "col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__name"
    }, sunrise), /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__value"
    }, selectedForecast.sunrise))), /*#__PURE__*/React.createElement("div", {
      className: "row currentCardDetailsItem"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cardImage"
    }, /*#__PURE__*/React.createElement(IconSunset, null)), /*#__PURE__*/React.createElement("div", {
      className: "col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__name"
    }, sunset), /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__value"
    }, selectedForecast.sunset))), /*#__PURE__*/React.createElement("div", {
      className: "row currentCardDetailsItem"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cardImage"
    }, /*#__PURE__*/React.createElement(IconTempMin, null)), /*#__PURE__*/React.createElement("div", {
      className: "col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__name"
    }, low), /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__value"
    }, selectedForecast[degreeMode].tempMin, "\xB0"))), /*#__PURE__*/React.createElement("div", {
      className: "row currentCardDetailsItem"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cardImage"
    }, /*#__PURE__*/React.createElement(IconTempMax, null)), /*#__PURE__*/React.createElement("div", {
      className: "col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__name"
    }, high), /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__value"
    }, selectedForecast[degreeMode].tempMax, "\xB0"))), /*#__PURE__*/React.createElement("div", {
      className: "row currentCardDetailsItem"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cardImage"
    }, /*#__PURE__*/React.createElement(IconHumidity, null)), /*#__PURE__*/React.createElement("div", {
      className: "col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__name"
    }, humidity), /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__value"
    }, `${selectedForecast.avgHumidity}%`))), /*#__PURE__*/React.createElement("div", {
      className: "row currentCardDetailsItem"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cardImage"
    }, /*#__PURE__*/React.createElement(IconWind, null)), /*#__PURE__*/React.createElement("div", {
      className: "col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__name"
    }, wind), /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__value"
    }, `${selectedForecast.maxWind} ${windSpeed}`)))) : null), /*#__PURE__*/React.createElement("div", {
      className: "weeklyForecastTitle"
    }, weeklyTitleText), /*#__PURE__*/React.createElement("div", {
      className: "col-lg-12"
    }, /*#__PURE__*/React.createElement("div", {
      ref: listRef,
      className: "row weeklyCardWrapper"
    }, !isDataProcessing ? weeklyForecast.map(forecast => /*#__PURE__*/React.createElement("div", {
      className: "row weeklyCardItem"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cardImage"
    }, /*#__PURE__*/React.createElement("img", {
      src: forecast.conditionIcon,
      alt: forecast.conditionText
    })), /*#__PURE__*/React.createElement("div", {
      className: "col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__name"
    }, forecast.dayOfWeek), /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__value"
    }, forecast[degreeMode].tempMax, "\xB0"), /*#__PURE__*/React.createElement("div", {
      className: "fr-wr-data-item__minValue"
    }, forecast[degreeMode].tempMin, "\xB0")))) : null)));
  };
  const variables = {
    "--weather-widget-background-color": getPageThemeColors(isVdsite).primary,
    "--weather-widget-background-color-rgb": hexToRgb(getPageThemeColors(isVdsite).primary, true),
    "--weather-widget-background-active-color": getPageThemeColors(isVdsite).primary_active,
    "--weather-widget-color": getColorByTheme(getPageThemeColors(isVdsite).primary)
  };
  return /*#__PURE__*/React.createElement(StyledWrapper, {
    rtl: rtl
  }, /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-xl-1"
  }), /*#__PURE__*/React.createElement("div", {
    className: "col-xl-11 selectedWeatherWidget",
    style: variables
  }, /*#__PURE__*/React.createElement(Text, {
    tag: "h3",
    field: heading,
    className: "col fr-wr-header"
  }), isDataProcessing ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "loader"
  }), /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-lg-5"
  }))) : null, !isDataProcessing && serviceError ? /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-lg-5 mb-3 mb-lg-0"
  }, serviceError)) : null, !isDataProcessing && error ? /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-lg-5 mb-3 mb-lg-0"
  }, error)) : null, !isDataProcessing && !error && !serviceError ? renderSuccess() : null)));
};
const StyledWrapper = _ref3 => {
  let {
    children,
    rtl
  } = _ref3;
  return /*#__PURE__*/React.createElement(ComponentsWrapper, {
    className: `${commonStyles} ${rtl ? rtlStyles : ltrStyles}`
  }, children);
};
const commonStyles = "c1pd2rk3";
const ltrStyles = "l1ae8rzr";
const rtlStyles = "r1nujl1v";
export default WeatherWidget;